import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from 'axios';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import ModalDialog from '../Dialog/ModalDialog';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Logo from './Logo/Logo';
import Profile from './Buttons/Profile';
import NewVersionNotice from '../../General/NewVersionNotice';
import Notifications from './Buttons/Notifications';
import AdminList from './Buttons/AdminList';
import UnlockChainList from './Buttons/UnlockChainList';
import Mail from './Buttons/Mail';
import BarcodeScanner from './Buttons/BarcodeScanner';
import GlobalSearch from './GlobalSearch';
import AutoCompleteField from '../AutoCompleteField/AutoCompleteField';
import SelectField from '../SelectField/SelectField';
import CheckboxField from '../CheckboxField/CheckboxField';
import TextField from '../TextField/TextField';
import { loginToTenant, impersonateUser } from '../../../actions/authentication';
import { useTranslation } from 'react-i18next';
import ValueHelper from '../../../helpers/valueHelper';

export default function Top(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const internalAdmin = useSelector(state => state.auth?.isInternalAdmin);
  const isInternal = useSelector(state => state.auth?.user?.internal);
  const isImpersonating = useSelector(state => state.auth?.isImpersonating);
  const impersonatedUser = useSelector(state => state.auth?.user);
  // Read-only tenant users get only the profile button in the app bar.
  const readOnly = useSelector(state => state.auth?.user?.readOnly === true);
  // Chain unlock is super-user only, so only they carry a redo list.
  const isSuper = useSelector(state => state.auth?.user?.isSuper === true);
  const adminList = useSelector(state => state.adminList?.changeList);
  const [tenants, setTenants] = useState([]);
  const tenant = useSelector(state => state.auth?.user?.tenantSel);
  const location = 'top';
  const { clicked, open } = props;

  // Add-tenant dialog state
  const [addTenantOpen, setAddTenantOpen] = useState(false);
  const [addTenantValues, setAddTenantValues] = useState({
    roleOptions: [], roleSels: [], roles: [],
    departmentOptions: [], depSels: [], department: [],
    processOptions: [], procSels: [], processes: [],
    locations: [], locSel: null, defaultLocation: null,
    timezones: [], timezoneSel: null, timezone: null,
    authTypes: [], authTypeSel: null, authType: null,
    hourlyRate: '', active: true, nonBillable: false, kiosk: false,
    allowNonBillable: false,
  });
  const [addTenantErrors, setAddTenantErrors] = useState({});
  const [addTenantSaving, setAddTenantSaving] = useState(false);

  // Impersonate-user dialog state
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [impersonateSearch, setImpersonateSearch] = useState('');
  const [impersonateUsers, setImpersonateUsers] = useState([]);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  useEffect(() => {
    const fetchTenants = async () => {
      const res = await axios.get('/int/tenantselectlist');
      setTenants(res.data);
    };
    if(internalAdmin)
      fetchTenants();
  }, [internalAdmin]);

  // Group tenants by status (active, then read only, then inactive),
  // sorted by name within each group.
  const tenantOptions = useMemo(() => {
    // rank drives both group ordering and the displayed status label
    const rankOf = (o) => o.active === false ? 2 : o.readOnly === true ? 1 : 0;
    const labelOf = (rank) => rank === 2 ? t('inactive') : rank === 1 ? t('readOnly') : t('active');
    return [...tenants]
      .map(o => ({ ...o, status: labelOf(rankOf(o)) }))
      .sort((a, b) => {
        const rankDiff = rankOf(a) - rankOf(b);
        if (rankDiff !== 0) return rankDiff;
        return (a.label || '').localeCompare(b.label || '');
      });
  }, [tenants, t]);

  const changeTenant = async (e, value) => {
    if(value != null) {
      let constants = await axios.get('/int/tenantjson/get/' + value.value);
      dispatch({ type: 'SET_TENANT', payload: { tenant: value.value ?? '', tenantSel: value ?? {}, constants: constants.data ?? {} } });
    }
    else {
      dispatch({ type: 'SET_TENANT', payload: { tenant: '', tenantSel: {}, constants: {} } });
    }
  };

  const handleLoginClick = useCallback(async () => {
    if (!tenant?.value) return;
    try {
      const res = await axios.get(`/users/hastenantentry/${tenant.value}`);
      if (res.data?.hasTenant) {
        dispatch(loginToTenant(tenant.value, '/#/home'));
      } else {
        // Load tenant-specific values for the dialog
        const [values, timezones] = await Promise.all([
          axios.get('/api/user/values', {
            params: { tenant: tenant.value, includeTimezones: false }
          }),
          ValueHelper.getTimezones()
        ]);
        const data = values.data;
        // Pre-select all roles and departments
        setAddTenantValues(prev => ({
          ...prev,
          roleOptions: data.roles || [],
          roleSels: data.roles || [],
          roles: (data.roles || []).map(r => r.value),
          departmentOptions: data.departments || [],
          depSels: data.departments || [],
          department: (data.departments || []).map(d => d.value),
          processOptions: data.processes || [],
          procSels: data.processes || [],
          processes: (data.processes || []).map(p => p.value),
          locations: data.locations || [],
          locSel: data.locations?.length === 1 ? data.locations[0] : null,
          defaultLocation: data.locations?.length === 1 ? data.locations[0].value : null,
          timezones: timezones,
          timezoneSel: null, timezone: null,
          authTypes: (() => {
            const allAuth = ValueHelper.authOptions();
            return (data.authTypes || []).map(raw => allAuth.find(a => a.value === raw)).filter(Boolean);
          })(),
          authTypeSel: null, authType: null,
          active: true, nonBillable: false, kiosk: false,
          hourlyRate: '',
          allowNonBillable: tenant.allowNonBillable === true,
        }));
        setAddTenantErrors({});
        setAddTenantOpen(true);
      }
    } catch {
      // If check fails, just try logging in directly
      dispatch(loginToTenant(tenant.value, '/#/home'));
    }
  }, [tenant, dispatch]);

  useEffect(() => {
    if (!impersonateOpen || !tenant?.value) return undefined;
    setImpersonateLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get('/int/users', {
          params: { tenant: tenant.value, search: impersonateSearch, size: 50 }
        });
        setImpersonateUsers(res.data?.data || []);
      } catch {
        setImpersonateUsers([]);
      } finally {
        setImpersonateLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [impersonateOpen, impersonateSearch, tenant]);

  const handleImpersonateClick = () => {
    if (!tenant?.value) return;
    setImpersonateSearch('');
    setImpersonateUsers([]);
    setImpersonateOpen(true);
  };

  const handleImpersonateSelect = (userId) => {
    setImpersonateOpen(false);
    dispatch(impersonateUser(tenant.value, userId, '/#/home'));
  };

  const handleAddTenantSave = async () => {
    const errors = {};
    if (!addTenantValues.depSels?.length) errors.departments = t('required');
    if (!addTenantValues.roleSels?.length) errors.roles = t('required');
    if (Object.keys(errors).length > 0) {
      setAddTenantErrors(errors);
      return;
    }
    setAddTenantSaving(true);
    try {
      await axios.post('/int/usertenantonly', {
        tenant: tenant.value,
        roles: addTenantValues.roles,
        department: addTenantValues.department,
        processes: addTenantValues.processes,
        defaultLocation: addTenantValues.defaultLocation,
        timezone: addTenantValues.timezone,
        active: true,
        hourlyRate: addTenantValues.hourlyRate,
        authType: addTenantValues.authType,
        nonBillable: addTenantValues.nonBillable,
        kiosk: addTenantValues.kiosk,
      });
      setAddTenantOpen(false);
      dispatch(loginToTenant(tenant.value, '/#/home'));
    } catch (err) {
      dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || 'Failed to add tenant', title: 'Error', severity: 'error' } });
    } finally {
      setAddTenantSaving(false);
    }
  };

  const handleAddTenantSkip = () => {
    setAddTenantOpen(false);
    dispatch(loginToTenant(tenant.value, '/#/home'));
  };

  const changeAutoTenant = (selName, valueName) => (_e, newValue) => {
    setAddTenantValues(prev => ({
      ...prev,
      [selName]: newValue,
      [valueName]: newValue?.value || null,
    }));
  };

  const changeMultiTenant = (selName, valueName, optionsName) => (_e, newValue) => {
    setAddTenantValues(prev => ({
      ...prev,
      [selName]: newValue || [],
      [valueName]: newValue ? newValue.map(v => v.value) : [],
    }));
  };

  const deleteMultiTenant = (selName, valueName) => (value) => {
    setAddTenantValues(prev => ({
      ...prev,
      [selName]: prev[selName].filter(v => v.value !== value),
      [valueName]: prev[valueName].filter(v => v !== value),
    }));
  };

  const topBar = (
    <AppBar
        enableColorOnDark
        position="fixed"
        variant='topAppBar'
        sx={(theme) => ({
          zIndex: theme.zIndex.drawer + 1,
          width: open ? `calc(100% - ${props.drawerWidth}px)` : '100vw',
          marginLeft: open ? props.drawerWidth : null,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen, }
          )
        })}
    >
      <Toolbar>
        {!isMobile &&
          <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={clicked}
              sx={{
                marginRight: 2,
                display: open ? 'none' : null
              }}
          >
              <MenuIcon/>
          </IconButton>
        }
        <Logo height="30" width="auto" alt="logo" maxHeight='50px' open={open} />
        <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ mr: 1 }}>
          {isImpersonating &&
            <Chip
              icon={<SwitchAccountIcon />}
              color="warning"
              size="small"
              label={`${t('impersonateUser')}: ${impersonatedUser?.firstName || ''} ${impersonatedUser?.lastName || ''} — ${t('readOnly')}`}
            />
          }
        </Typography>
        {!internalAdmin && <GlobalSearch />}
        {internalAdmin && <Box sx={{ flexGrow: 1 }} />}
        {!isMobile && !internalAdmin && !readOnly &&
          <>
            <BarcodeScanner />
            {isInternal && adminList?.length > 0 && <AdminList />}
            {isSuper && <UnlockChainList />}
            {/* <Activity location={location} /> */}
            <Mail location={location} />
            {!internalAdmin && <Notifications location={location} />}
          </>
        }
        {internalAdmin &&
          <AutoCompleteField
            value={tenant}
            options={tenantOptions}
            groupBy='status'
            onChange={changeTenant}
            label='Tenant'
            style={{
              width: '300px'
            }}
          />
        }
        {internalAdmin && tenant != null &&
          <IconButton onClick={handleLoginClick}>
            <LoginRoundedIcon color='secondary'/>
          </IconButton>
        }
        {internalAdmin && tenant != null &&
          <Tooltip title={t('impersonateUser')}>
            <IconButton onClick={handleImpersonateClick}>
              <SwitchAccountIcon color='secondary'/>
            </IconButton>
          </Tooltip>
        }
        <NewVersionNotice />
        <Profile nodeEnv={props.nodeEnv}/>
      </Toolbar>
    </AppBar>
  );

  return (
    <>
      {topBar}
      <ModalDialog
        dialogStatus={addTenantOpen}
        toggleDialog={() => setAddTenantOpen(false)}
        title={`Add ${tenant?.label || 'Tenant'} to your account`}
        maxWidth="md"
        fullWidth
        buttonStack={(
          <>
            <Button onClick={handleAddTenantSkip} color="inherit">{t('skip', 'Skip')}</Button>
            <Button variant="contained" onClick={handleAddTenantSave} disabled={addTenantSaving}>{t('save', 'Save & Login')}</Button>
          </>
        )}
      >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You don't have a user entry for this tenant yet. Set up your roles and departments, or skip to log in with admin defaults.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <AutoCompleteField
                value={addTenantValues.locSel}
                options={addTenantValues.locations}
                onChange={changeAutoTenant('locSel', 'defaultLocation')}
                label={t('defaultLocation')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <AutoCompleteField
                value={addTenantValues.timezoneSel}
                options={addTenantValues.timezones}
                onChange={changeAutoTenant('timezoneSel', 'timezone')}
                label={t('timezone')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <AutoCompleteField
                value={addTenantValues.authTypeSel}
                options={addTenantValues.authTypes}
                onChange={changeAutoTenant('authTypeSel', 'authType')}
                label={t('authentication')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                value={addTenantValues.hourlyRate}
                onChange={(e) => setAddTenantValues(prev => ({ ...prev, hourlyRate: e.target.value }))}
                name="hourlyRate"
                label={t('hourlyRate')}
                fullWidth
              />
            </Grid>
            {addTenantValues.departmentOptions.length > 0 && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  selectAll
                  value={addTenantValues.department || []}
                  valueObjects={addTenantValues.depSels}
                  options={addTenantValues.departmentOptions}
                  label={t('departments')}
                  selectAllLabel={t('selectAll')}
                  error={!!addTenantErrors.departments}
                  helperText={addTenantErrors.departments}
                  onChange={changeMultiTenant('depSels', 'department', 'departmentOptions')}
                  onDelete={deleteMultiTenant('depSels', 'department')}
                />
              </Grid>
            )}
            {addTenantValues.roleOptions.length > 0 && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  selectAll
                  value={addTenantValues.roles || []}
                  valueObjects={addTenantValues.roleSels}
                  options={addTenantValues.roleOptions}
                  label={t('roles')}
                  selectAllLabel={t('selectAll')}
                  error={!!addTenantErrors.roles}
                  helperText={addTenantErrors.roles}
                  onChange={changeMultiTenant('roleSels', 'roles', 'roleOptions')}
                  onDelete={deleteMultiTenant('roleSels', 'roles')}
                />
              </Grid>
            )}
            {addTenantValues.processOptions.length > 0 && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  selectAll
                  value={addTenantValues.processes || []}
                  valueObjects={addTenantValues.procSels}
                  options={addTenantValues.processOptions}
                  label={t('businessProcesses')}
                  selectAllLabel={t('selectAll')}
                  onChange={changeMultiTenant('procSels', 'processes', 'processOptions')}
                  onDelete={deleteMultiTenant('procSels', 'processes')}
                />
              </Grid>
            )}
          </Grid>
      </ModalDialog>
      <ModalDialog
        dialogStatus={impersonateOpen}
        toggleDialog={() => setImpersonateOpen(false)}
        title={t('impersonateUser')}
        maxWidth="sm"
        fullWidth
      >
        <TextField
          value={impersonateSearch}
          onChange={(e) => setImpersonateSearch(e.target.value)}
          name="impersonateSearch"
          label={t('search')}
          fullWidth
          autoFocus
          sx={{ mb: 2 }}
        />
        {impersonateLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List sx={{ maxHeight: 360, overflow: 'auto' }}>
            {impersonateUsers.length > 0 ? impersonateUsers.map((u) => (
              <ListItemButton key={u._id} onClick={() => handleImpersonateSelect(u._id)}>
                <ListItemText
                  primary={`${u.firstName} ${u.lastName}`}
                  secondary={u.email}
                />
              </ListItemButton>
            )) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                {t('noRecords')}
              </Typography>
            )}
          </List>
        )}
      </ModalDialog>
    </>
  );
}
